/**
 * Nosto Personalized Cards Popup
 * Handles personalized product additions from Nosto recommendations
 */

if (!window.Eurus.loadedScript.has('nosto-personalized-cards-popup.js')) {
  window.Eurus.loadedScript.add('nosto-personalized-cards-popup.js');
  
  // Store reference to modal instance
  window.nostoModalInstance = null;
  
  // Make openProductPopup available immediately
  window.openProductPopup = function(product) {
    if (window.nostoModalInstance) {
      window.nostoModalInstance.openProductPopup(product);
      return;
    }
    
    // Try to find modal element and get Alpine instance
    const modalElement = document.querySelector('#NostoPersonalizedCardsPopup');
    if (modalElement && window.Alpine) {
      // Check if Alpine has initialized this element (__x is Alpine's internal property)
      if (modalElement.__x) {
        window.nostoModalInstance = Alpine.$data(modalElement);
        if (window.nostoModalInstance) {
          window.nostoModalInstance.openProductPopup(product);
          return;
        }
      } else {
        // Wait for Alpine to initialize, then try again
        const checkAlpine = () => {
          if (modalElement.__x) {
            window.nostoModalInstance = Alpine.$data(modalElement);
            if (window.nostoModalInstance) {
              window.nostoModalInstance.openProductPopup(product);
            }
          }
        };
        
        // Listen for Alpine initialization
        if (document.readyState === 'loading') {
          document.addEventListener('alpine:initialized', checkAlpine, { once: true });
        } else {
          // Alpine might already be initialized, check immediately
          requestAnimationFrame(checkAlpine);
        }
      }
    }
  };
  
  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.data('xNostoPersonalizedCardsPopup', () => ({
        selectedProduct: null,
        showPopup: false,
        loading: false,
        errorMessage: '',
        personalization: {
          to: '',
          from: '',
          message: ''
        },
        pimEndpointUrl: null,
        cdnEndpointUrl: null,
        currency: null,
        locale: null,
        moneyFormat: null,
        labels: {},
        productId: null,
        pimOptions: null,
        selectedValues: {},
        loadingPersonalisation: false,
        hasPersonalisation: false,

        init() {
          window.nostoModalInstance = this;
          const d = this.$el.dataset;
          this.pimEndpointUrl = d.pimUrl || null;
          this.cdnEndpointUrl = d.cdnUrl || null;
          this.currency = d.currency || 'GBP';
          this.locale = d.locale || '';
          this.moneyFormat = d.moneyFormat || '';
          this.labels = {
            fontStyle: d.labelFontStyle || 'Font',
            fontColour: d.labelFontColour || 'Font Colour'
          };
        },

        openProductPopup(product) {
          this.selectedProduct = product;
          this.productId = product.id ? String(product.id) : null;
          this.showPopup = true;
          this.personalization = { to: '', from: '', message: '' };
          this.selectedValues = {};
          this.pimOptions = null;
          this.hasPersonalisation = false;
          document.body.classList.add('overflow-hidden');
          this.fetchPersonalisationData();
        },

        closePopup() {
          this.showPopup = false;
          this.selectedProduct = null;
          this.productId = null;
          this.pimOptions = null;
          this.selectedValues = {};
          this.hasPersonalisation = false;
          this.loading = false;
          this.errorMessage = '';
          this.personalization = {
            to: '',
            from: '',
            message: ''
          };
          document.body.classList.remove('overflow-hidden');
        },

        async fetchPersonalisationData() {
          if (!this.productId || (!this.pimEndpointUrl && !this.cdnEndpointUrl)) {
            return;
          }
          this.loadingPersonalisation = true;
          try {
            if (this.pimEndpointUrl) {
              const data = await this.fetchWithTimeout(
                (signal) => this.fetchFromPIM(signal),
                3000
              );
              this.processPersonalisationData(data);
              this.hasPersonalisation = this.pimOptions && this.pimOptions.length > 0;
            }
          } catch (err) {
            if (this.cdnEndpointUrl) {
              try {
                const data = await this.fetchFromCDN();
                this.processPersonalisationData(data);
                this.hasPersonalisation = this.pimOptions && this.pimOptions.length > 0;
              } catch (e) {
                console.warn('Nosto popup: PIM and CDN fetch failed', e);
              }
            }
          }
          this.loadingPersonalisation = false;
        },

        fetchWithTimeout(fetchFn, timeout) {
          const controller = new AbortController();
          const t = setTimeout(() => controller.abort(), timeout);
          return fetchFn(controller.signal).finally(() => clearTimeout(t));
        },

        async fetchFromPIM(signal) {
          const url = `${this.pimEndpointUrl}personalisation/${this.productId}?currency=${this.currency}`;
          const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' }, signal });
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        },

        async fetchFromCDN() {
          const url = `${this.cdnEndpointUrl}configs/product-${this.productId}.json`;
          const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return this.transformCDNData(await res.json());
        },

        transformCDNData(cdnData) {
          const currencyData = cdnData.currencies?.[this.currency] || cdnData.currencies?.GBP;
          let translations = cdnData.translations?.[this.locale];
          if (!translations && this.locale) {
            const prefix = this.locale.split('-')[0];
            const key = Object.keys(cdnData.translations || {}).find((k) => k.startsWith(prefix));
            translations = key ? cdnData.translations[key] : null;
          }
          translations = translations || cdnData.translations?.[cdnData.base_locale] || {};
          const options = [];
          if (cdnData.options?.text_lines) {
            cdnData.options.text_lines.forEach((textLine) => {
              const tr = translations[textLine.label_key] || {};
              const priceMod = this.calculatePriceForCurrency(textLine.price_gbp, cdnData.currencies, this.currency);
              options.push({
                type: textLine.type,
                label: tr.label || textLine.label_key,
                placeholder: tr.placeholder || '',
                key: textLine.key,
                char_limit: textLine.char_limit,
                required: textLine.required,
                price_modifier: priceMod
              });
            });
          }
          if (cdnData.options?.fonts?.length) {
            options.push({
              type: 'select',
              label: this.labels.fontStyle,
              key: 'font',
              required: false,
              choices: cdnData.options.fonts.map((f) => ({ label: f.label, value: f.value, is_default: f.is_default || false }))
            });
          }
          if (cdnData.options?.font_colours?.length) {
            options.push({
              type: 'select',
              label: this.labels.fontColour,
              key: 'font_colour',
              required: false,
              choices: cdnData.options.font_colours.map((c) => ({ label: c.label, value: c.value, hex: c.hex, is_default: c.is_default || false }))
            });
          }
          return { options };
        },

        calculatePriceForCurrency(priceGBP, currencies, targetCurrency) {
          if (!priceGBP) return 0;
          const data = currencies?.[targetCurrency];
          return data ? priceGBP * data.exchange_rate_from_gbp : priceGBP;
        },

        processPersonalisationData(data) {
          if (!data?.options?.length) return;
          this.pimOptions = data.options;
          this.pimOptions.forEach((option) => {
            if (option.key?.startsWith('_')) option.key = option.key.slice(1);
            if (option.choices?.length) {
              const single = option.choices.length === 1;
              const def = option.choices.find((c) => c.is_default);
              this.selectedValues[option.key] = single ? option.choices[0].value : (def ? def.value : '');
            }
          });
        },

        getOptionLabel(option) {
          if (option.key === 'font') return this.labels.fontStyle;
          if (option.key === 'font_colour') return this.labels.fontColour;
          return option.label || option.key;
        },

        getValueLabel(option, value) {
          if (option.type === 'text') return value;
          const choice = option.choices?.find((c) => c.value === value);
          return choice?.label || value;
        },

        calculateTotalPriceModifier() {
          if (!this.pimOptions?.length) return 0;
          let total = 0;
          this.pimOptions.forEach((opt) => {
            if (this.selectedValues[opt.key] && opt.price_modifier) total += opt.price_modifier;
          });
          return total;
        },

        isSingleOption(option) {
          return option.choices && option.choices.length === 1;
        },

        isLongTextOption(option) {
          return option.type === 'text' && (option.char_limit || 0) > 100;
        },

        getCharLimitText(option) {
          if (!option || option.type !== 'text') return '';
          const limit = option.char_limit || (this.isLongTextOption(option) ? 500 : 50);
          return limit + ' characters';
        },

        getOptionValue(key) {
          return this.selectedValues[key] || '';
        },

        setOptionValue(key, value) {
          this.selectedValues[key] = value;
        },

        buildCartProperties() {
          if (this.hasPersonalisation && this.pimOptions?.length) {
            const props = {};
            Object.entries(this.selectedValues).forEach(([key, value]) => {
              if (!value) return;
              const option = this.pimOptions.find((o) => o.key === key);
              const technicalKey = key.startsWith('_') ? key : '_' + key;
              let label = option ? this.getOptionLabel(option) : key;
              const valueLabel = option ? this.getValueLabel(option, value) : value;
              props[technicalKey] = value;
              if (option && this.isSingleOption(option) && !label.startsWith('_')) label = '_' + label;
              props[label] = valueLabel;
            });
            props._personalisation_applied = "1";
            const modifier = this.calculateTotalPriceModifier();
            if (modifier > 0) props._personalisation_total_modifier = String(Math.round(modifier * 100));
            return props;
          }
          return {
            'To': this.personalization.to || '',
            'From': this.personalization.from || '',
            'Message': this.personalization.message || ''
          };
        },

        async addToCart() {
          if (!this.selectedProduct) {
            return;
          }

          this.loading = true;
          this.errorMessage = '';

          try {
            const variantId = this.selectedProduct.variantId;

            if (!variantId) {
              throw new Error('No variant ID available for this product');
            }

            const items = [{
              id: variantId,
              quantity: 1,
              properties: this.buildCartProperties()
            }];

            // Add to cart via Shopify API
            const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                items: items,
                sections: window.Alpine && Alpine.store('xCartHelper') 
                  ? Alpine.store('xCartHelper').getSectionsToRender().map((section) => section.id)
                  : [],
                sections_url: window.location.pathname
              })
            });

            if (!response.ok) {
              const error = await response.json();
              throw new Error(error.description || error.message || 'Failed to add to cart');
            }

            const data = await response.json();

            if (data.sections && window.Alpine && Alpine.store('xCartHelper')) {
              Alpine.store('xCartHelper').swapSections(data.sections);
            }

            // Dispatch custom events for cart updates
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            
            // Show cart drawer if available
            if (window.Alpine && Alpine.store('xMiniCart')) {
              Alpine.store('xMiniCart').open = true;
            }

            // Close the popup
            this.closePopup();

          } catch (error) {
            console.error('Error adding product to cart:', error);
            this.errorMessage = error.message || 'Failed to add product to cart. Please try again.';
            this.loading = false;
          }
        }
      }));
    });
  });
}

